<template>
  <AppSidebar>
    <div class="space-y-10 pb-12">
      <!-- Page Header -->
      <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-8 pb-4">
        <div class="space-y-2">
            <div class="flex items-center gap-3 mb-1">
                <div class="w-2 h-2 bg-primary-500 rounded-full shadow-[0_0_12px_rgba(20,184,166,0.5)] animate-pulse"></div>
                <span class="text-[10px] font-bold text-primary-600 dark:text-primary-400 uppercase tracking-widest">About Management</span>
            </div>
            <h1 class="text-4xl font-bold text-gray-900 dark:text-white uppercase tracking-tighter sm:text-5xl" style="font-family: Poppins, sans-serif">
                About Us <span class="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-blue-500">Settings</span>
            </h1>
            <p class="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-widest leading-loose max-w-md">
                Configure your brand story, team members, mission, and company statistics.
            </p>
        </div>
      </div>

      <!-- Main Interface container -->
      <div class="bg-white dark:bg-gray-800/40 rounded-[40px] border border-gray-100 dark:border-gray-700/50 shadow-2xl shadow-gray-200/50 dark:shadow-none overflow-hidden backdrop-blur-xl">
        <!-- Premium Tabs -->
        <div class="border-b border-gray-100 dark:border-gray-800/50 px-8 py-2 bg-gray-50/30 dark:bg-gray-900/40">
          <nav class="flex gap-8 overflow-x-auto custom-scrollbar">
            <button v-for="tab in tabs" :key="tab.id" 
              @click="activeTab = tab.id"
              class="relative py-4 text-[10px] font-black uppercase tracking-widest transition-all duration-300 group whitespace-nowrap"
              :class="activeTab === tab.id ? 'text-primary-500' : 'text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-gray-300'">
              {{ tab.name }}
              <div class="absolute bottom-0 left-0 h-0.5 bg-primary-500 transition-all duration-300"
                  :class="activeTab === tab.id ? 'w-full' : 'w-0 group-hover:w-1/2'"></div>
            </button>
          </nav>
        </div>

        <div class="p-8 sm:p-12">
          <!-- Loading State -->
          <div v-if="loading" class="flex flex-col items-center justify-center py-24 space-y-4">
            <div class="relative w-16 h-16">
              <div class="absolute inset-0 border-4 border-primary-500/20 rounded-full"></div>
              <div class="absolute inset-0 border-4 border-primary-500 rounded-full border-t-transparent animate-spin"></div>
            </div>
            <span class="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Loading Content...</span>
          </div>

          <div v-else class="animate-in fade-in slide-in-from-bottom-2 duration-500">
            <!-- Hero Section Tab -->
            <div v-if="activeTab === 'hero'" class="max-w-4xl space-y-10">
                <div class="flex items-center gap-4 mb-2">
                    <div class="p-3 bg-primary-50 dark:bg-primary-900/20 rounded-2xl">
                      <RocketLaunchIcon class="w-5 h-5 text-primary-500" />
                    </div>
                     <div>
                      <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">Page Hero</h3>
                      <p class="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-0.5">Primary brand heading section</p>
                    </div>
                </div>

                <form @submit.prevent="saveSection('hero')" class="space-y-8">
                   <div class="group">
                    <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Main Heading</label>
                    <input v-model="sections.hero.data.title" type="text" 
                        class="w-full px-5 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[12px] font-bold uppercase tracking-widest text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all shadow-sm" />
                  </div>
                   <div class="group">
                    <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Sub-Heading</label>
                    <textarea v-model="sections.hero.data.subtitle" rows="4"
                      class="w-full px-6 py-5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-[32px] text-[12px] font-medium tracking-wider text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all resize-none shadow-sm leading-relaxed"></textarea>
                  </div>
                  <div class="flex justify-end pt-4">
                    <button type="submit" 
                      class="group relative inline-flex items-center px-10 py-4 bg-gray-900 dark:bg-primary-600 text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-2xl hover:scale-105 transition-all shadow-xl shadow-primary-500/20 active:scale-95 overflow-hidden">
                       <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                      Save Hero
                    </button>
                  </div>
                </form>
            </div>

            <!-- Company Story Tab -->
            <div v-if="activeTab === 'company_story'" class="max-w-4xl space-y-10">
                <div class="flex items-center gap-4 mb-2">
                    <div class="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-2xl">
                      <BookOpenIcon class="w-5 h-5 text-blue-500" />
                    </div>
                     <div>
                      <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">Our Story</h3>
                      <p class="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-0.5">Manage your company history</p>
                    </div>
                </div>

                <form @submit.prevent="saveSection('company_story')" class="space-y-8">
                   <div class="group">
                    <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Section Title</label>
                    <input v-model="sections.company_story.data.title" type="text" 
                        class="w-full px-5 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[12px] font-bold uppercase tracking-widest text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all shadow-sm" />
                  </div>
                   <div class="group">
                    <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Story Description</label>
                    <textarea v-model="sections.company_story.data.description" rows="6"
                      class="w-full px-6 py-5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-[32px] text-[12px] font-medium leading-relaxed text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all resize-none shadow-sm"></textarea>
                  </div>
                   <div class="group bg-gray-50/50 dark:bg-gray-900/20 p-8 rounded-[32px] border border-gray-100 dark:border-gray-700/50">
                    <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-4 px-1">Section Image</label>
                    <ImageUpload
                      v-model="companyStoryImageFile"
                      :model-value="sections.company_story.data.image_url"
                      @change="handleCompanyStoryImageChange"
                      class="dark:bg-gray-900/40 rounded-2xl"
                    />
                  </div>
                  <div class="flex justify-end pt-4">
                    <button type="submit" 
                      class="group relative inline-flex items-center px-10 py-4 bg-gray-900 dark:bg-primary-600 text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-2xl hover:scale-105 transition-all shadow-xl shadow-primary-500/20 active:scale-95 overflow-hidden font-sans">
                       <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                      Save Story
                    </button>
                  </div>
                </form>
            </div>

            <!-- Mission & Vision Tab -->
            <div v-if="activeTab === 'mission_vision'" class="max-w-4xl space-y-12">
                <div class="flex items-center gap-4 mb-2">
                    <div class="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl">
                      <SparklesIcon class="w-5 h-5 text-emerald-500" />
                    </div>
                     <div>
                      <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">Mission & Vision</h3>
                      <p class="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-0.5">Define your core purpose and values</p>
                    </div>
                </div>

                <form @submit.prevent="saveSection('mission_vision_values')" class="space-y-8">
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                     <div class="group">
                      <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Section Title</label>
                      <input v-model="sections.mission_vision_values.data.title" type="text"
                          class="w-full px-5 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[12px] font-bold uppercase tracking-widest text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all shadow-sm" />
                    </div>
                     <div class="group">
                      <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Description</label>
                      <input v-model="sections.mission_vision_values.data.description" type="text"
                          class="w-full px-5 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[12px] font-medium text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all shadow-sm" />
                    </div>
                  </div>
                  
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                     <div class="group">
                      <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Our Mission</label>
                      <textarea v-model="sections.mission_vision_values.data.mission" rows="4"
                        class="w-full px-6 py-5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-[32px] text-[12px] font-medium leading-relaxed text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all resize-none shadow-sm"></textarea>
                    </div>
                     <div class="group">
                      <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Our Vision</label>
                      <textarea v-model="sections.mission_vision_values.data.vision" rows="4"
                        class="w-full px-6 py-5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-[32px] text-[12px] font-medium leading-relaxed text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all resize-none shadow-sm"></textarea>
                    </div>
                  </div>

                   <div class="group">
                    <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Core Values (One per line)</label>
                    <textarea v-model="missionValues" rows="4"
                      class="w-full px-6 py-5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-[32px] text-xs font-bold uppercase tracking-widest text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all resize-none shadow-sm leading-relaxed"></textarea>
                  </div>

                  <div class="flex justify-end pt-4">
                    <button type="submit" 
                      class="group relative inline-flex items-center px-10 py-4 bg-gray-900 dark:bg-primary-600 text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-2xl hover:scale-105 transition-all shadow-xl shadow-primary-500/20 active:scale-95 overflow-hidden">
                       <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                      Save Mission/Vision
                    </button>
                  </div>
                </form>
            </div>

            <!-- Team Members Tab -->
            <div v-if="activeTab === 'team'" class="space-y-12">
                <div class="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div class="flex items-center gap-4">
                        <div class="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-2xl">
                          <UserGroupIcon class="w-5 h-5 text-indigo-500" />
                        </div>
                         <div>
                          <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">Team Directory</h3>
                          <p class="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-0.5">Manage your team members</p>
                        </div>
                    </div>
                     <button @click="openTeamModal()" 
                      class="inline-flex items-center px-6 py-3 bg-primary-500 text-white text-[10px] font-bold uppercase tracking-widest rounded-xl hover:scale-105 transition-all active:scale-95 shadow-xl shadow-primary-500/20">
                      <PlusIcon class="w-4 h-4 mr-2" />
                      Add Team Member
                    </button>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <div v-for="member in teamMembers" :key="member.id" 
                      class="group bg-white dark:bg-gray-900/30 border border-gray-100 dark:border-gray-800 rounded-[40px] p-8 transition-all hover:bg-gray-50 dark:hover:bg-gray-800/50 hover:-translate-y-2 relative overflow-hidden">
                        <div class="flex flex-col items-center text-center relative z-10">
                            <div class="w-32 h-32 bg-gray-50 dark:bg-gray-900 rounded-[32px] mx-auto mb-6 overflow-hidden border border-gray-100 dark:border-gray-700 shadow-inner group-hover:border-primary-500/50 transition-colors">
                                <img v-if="member.image_url" :src="member.image_url" 
                                     class="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 scale-110 group-hover:scale-100" />
                                <div v-else class="w-full h-full flex items-center justify-center text-gray-200 dark:text-gray-700 text-4xl font-black uppercase">
                                  {{ member.name.charAt(0) }}
                                </div>
                            </div>
                             <h4 class="text-lg font-bold text-gray-900 dark:text-white tracking-tighter mb-1" style="font-family: Poppins, sans-serif">{{ member.name }}</h4>
                            <div class="text-[10px] font-bold text-primary-500 uppercase tracking-widest mb-4 italic">{{ member.position }}</div>
                            <p class="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed max-w-[240px] line-clamp-2 tracking-tight h-8 mb-8">{{ member.bio }}</p>
                            
                            <div class="flex gap-4 pt-6 border-t border-gray-50 dark:border-gray-800/50 w-full justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <button @click="openTeamModal(member)" class="p-2 text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-xl transition-all">
                                    <PencilSquareIcon class="w-5 h-5" />
                                </button>
                                <button @click="deleteTeamMember(member.id)" class="p-2 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-all">
                                    <TrashIcon class="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Statistics Tab -->
            <div v-if="activeTab === 'statistics'" class="space-y-12">
                <div class="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div class="flex items-center gap-4">
                        <div class="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-2xl">
                          <ChartBarIcon class="w-5 h-5 text-amber-500" />
                        </div>
                         <div>
                          <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">Business Statistics</h3>
                          <p class="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-0.5">Company growth and reach indicators</p>
                        </div>
                    </div>
                     <button @click="openStatModal()" 
                      class="inline-flex items-center px-6 py-3 bg-primary-500 text-white text-[10px] font-bold uppercase tracking-widest rounded-xl hover:scale-105 transition-all active:scale-95 shadow-xl shadow-primary-500/20">
                      <PlusIcon class="w-4 h-4 mr-2" />
                      Add Statistic
                    </button>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    <div v-for="stat in statistics" :key="stat.id" 
                      class="group p-10 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-800 rounded-[40px] text-center transition-all hover:bg-white dark:hover:bg-gray-800/50 hover:shadow-xl hover:shadow-gray-200/50 dark:hover:shadow-none">
                       <div class="text-5xl font-bold text-primary-500 mb-2 font-mono tracking-tighter group-hover:scale-110 transition-transform italic">{{ stat.value }}</div>
                       <div class="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">{{ stat.label }}</div>
                       
                       <div class="flex justify-center gap-4 mt-10 opacity-0 group-hover:opacity-100 transition-opacity">
                         <button @click="openStatModal(stat)" class="text-[10px] font-bold text-primary-500 uppercase tracking-widest hover:underline">Edit</button>
                         <button @click="deleteStatistic(stat.id)" class="text-[10px] font-bold text-rose-500 uppercase tracking-widest hover:underline">Delete</button>
                       </div>
                    </div>
                </div>
            </div>
          </div>
        </div>
      </div>

    <!-- Team Member Modal -->
    <div v-if="showTeamModal" class="fixed inset-0 z-[60] overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
        <div class="fixed inset-0 bg-gray-900/40 dark:bg-black/60 backdrop-blur-md transition-opacity" @click="closeTeamModal"></div>
        <div class="relative inline-block bg-white dark:bg-gray-900 rounded-[40px] text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:max-w-3xl sm:w-full border border-white/20 dark:border-gray-800/50">
           <div class="px-10 py-8 border-b border-gray-100 dark:border-gray-800/50">
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white uppercase tracking-tighter" style="font-family: Poppins, sans-serif">
              {{ editingMember ? 'Edit Team Member' : 'Add Team Member' }}
            </h3>
            <p class="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-1">Enter member details below</p>
          </div>

          <div class="px-10 py-8">
              <!-- Form Tabs -->
              <div class="flex gap-8 border-b border-gray-100 dark:border-gray-800/50 mb-10 overflow-x-auto custom-scrollbar">
                 <button v-for="tTab in ['basic', 'social']" :key="tTab" @click="activeTeamTab = tTab"
                  class="group relative py-4 text-[10px] font-bold uppercase tracking-widest transition-all whitespace-nowrap"
                  :class="activeTeamTab === tTab ? 'text-primary-500' : 'text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-gray-300'"
                >
                  {{ tTab === 'basic' ? 'Basic Info' : 'Social Links' }}
                  <div class="absolute bottom-0 left-0 h-0.5 bg-primary-500 transition-all duration-300"
                      :class="activeTeamTab === tTab ? 'w-full' : 'w-0 group-hover:w-1/2'"></div>
                </button>
              </div>

              <form @submit.prevent="saveTeamMember" class="space-y-8">
                <!-- Basic Info Tab -->
                <div v-show="activeTeamTab === 'basic'" class="space-y-8 animate-in fade-in slide-in-from-bottom-1 duration-300">
                   <div class="group bg-gray-50/50 dark:bg-gray-900/20 p-8 rounded-[32px] border border-gray-100 dark:border-gray-700/50">
                    <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-4 px-1">Profile Photo</label>
                    <ImageUpload
                      v-model="profileImageFile"
                      :model-value="editingMember?.image_url"
                      @change="handleProfileImageChange"
                      class="dark:bg-gray-900/40 rounded-2xl"
                    />
                  </div>

                   <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div class="group">
                      <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Full Name</label>
                      <input v-model="teamForm.name" type="text" required
                          class="w-full px-5 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[12px] font-medium text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all" />
                    </div>
                    <div class="group">
                      <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Position</label>
                      <input v-model="teamForm.position" type="text" required
                          class="w-full px-5 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[12px] font-medium text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all" />
                    </div>
                  </div>

                   <div class="group">
                    <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Biography</label>
                    <textarea v-model="teamForm.bio" rows="4"
                      class="w-full px-6 py-5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-[32px] text-[12px] font-medium text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all resize-none leading-relaxed"></textarea>
                  </div>

                  <div class="flex items-center justify-center p-6 bg-gray-50/50 dark:bg-gray-900/10 rounded-2xl border border-gray-100 dark:border-gray-700/50">
                    <label class="relative inline-flex items-center cursor-pointer group">
                      <input type="checkbox" v-model="teamForm.is_active" class="sr-only peer">
                       <div class="w-12 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-500"></div>
                      <span class="ml-3 text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Active Status</span>
                    </label>
                  </div>
                </div>

                <!-- Neural Linkage Tab -->
                <div v-show="activeTeamTab === 'social'" class="space-y-6 animate-in fade-in slide-in-from-bottom-1 duration-300">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                         <div v-for="(link, platform) in socialLinks" :key="platform" class="group">
                             <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">{{ platform }}</label>
                            <input v-model="socialLinks[platform]" type="url" :placeholder="`Enter ${platform} URL`"
                                class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-xl text-[11px] font-medium text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all" />
                        </div>
                    </div>
                </div>

                 <div class="flex justify-end gap-4 pt-4 border-t border-gray-100 dark:border-gray-800/50">
                    <button type="button" @click="closeTeamModal" class="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-900 dark:hover:text-white transition-all">Cancel</button>
                    <button type="submit" :disabled="saving"
                       class="px-8 py-4 bg-primary-500 text-white text-[10px] font-bold uppercase tracking-widest rounded-2xl shadow-xl shadow-primary-500/20 active:scale-95 transition-all disabled:opacity-50">
                       <span v-if="saving">Saving...</span>
                       <span v-else>{{ editingMember ? 'Save Member' : 'Create Member' }}</span>
                    </button>
                 </div>
              </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Statistic Modal -->
    <div v-if="showStatModal" class="fixed inset-0 z-[60] overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
        <div class="fixed inset-0 bg-gray-900/40 dark:bg-black/60 backdrop-blur-md transition-opacity" @click="closeStatModal"></div>
        <div class="relative inline-block bg-white dark:bg-gray-900 rounded-[40px] text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:max-w-md sm:w-full border border-white/20 dark:border-gray-800/50">
           <div class="px-10 py-8 border-b border-gray-100 dark:border-gray-800/50">
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white uppercase tracking-tighter" style="font-family: Poppins, sans-serif">
              {{ editingStat ? 'Edit Statistic' : 'Create Statistic' }}
            </h3>
            <p class="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-1">Enter statistic details below</p>
          </div>
          <form @submit.prevent="saveStatistic" class="p-10 space-y-6">
             <div class="group">
              <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">Statistic Label</label>
              <input v-model="statForm.label" type="text" required class="w-full px-5 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[12px] font-bold uppercase tracking-widest text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 shadow-sm" />
            </div>
            <div class="group">
              <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">Value (e.g., 500+)</label>
              <input v-model="statForm.value" type="text" required class="w-full px-5 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[12px] font-bold uppercase tracking-widest text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 shadow-sm" />
            </div>
            <div class="flex items-center justify-center p-6 bg-gray-50/50 dark:bg-gray-900/10 rounded-2xl border border-gray-100 dark:border-gray-700/50">
              <label class="relative inline-flex items-center cursor-pointer group">
                <input type="checkbox" v-model="statForm.is_active" class="sr-only peer">
                 <div class="w-12 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-500"></div>
                <span class="ml-3 text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Active Status</span>
              </label>
            </div>
             <div class="flex justify-end gap-4 pt-4">
                <button type="button" @click="closeStatModal" class="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-900 dark:hover:text-white transition-all">Cancel</button>
                <button type="submit" class="px-8 py-4 bg-primary-500 text-white text-[10px] font-bold uppercase tracking-widest rounded-2xl shadow-xl shadow-primary-500/20 active:scale-95 transition-all">Save Statistic</button>
             </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</AppSidebar>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { 
    RocketLaunchIcon, BookOpenIcon, SparklesIcon, 
    UserGroupIcon, ChartBarIcon, PlusIcon, 
    PencilSquareIcon, TrashIcon 
} from '@heroicons/vue/24/outline'
import { useContentStore } from '@/stores/content'
import { useTeamMembersStore } from '@/stores/teamMembers'
import { useStatisticsStore } from '@/stores/statistics'
import AppSidebar from '@/components/layout/AppSidebar.vue'
import ImageUpload from '@/components/ImageUpload.vue'

const contentStore = useContentStore()
const teamStore = useTeamMembersStore()
const statStore = useStatisticsStore()

const loading = ref(true)
const saving = ref(false)
const activeTab = ref('hero')
const activeTeamTab = ref('basic')
const showTeamModal = ref(false)
const editingMember = ref(null)
const showStatModal = ref(false)
const editingStat = ref(null)

// Image upload states
const profileImageFile = ref(null)
const companyStoryImageFile = ref(null)

// Form state
const teamForm = ref({
  name: '',
  position: '',
  bio: '',
  image: '',
  social_links: {},
  is_active: true
})

const socialLinks = ref({
  linkedin: '',
  twitter: '',
  github: '',
  facebook: '',
  instagram: '',
  website: ''
})

const statForm = ref({
  label: '',
  value: '',
  icon: '',
  is_active: true
})

const tabs = [
  { id: 'hero', name: 'Hero Section' },
  { id: 'company_story', name: 'Company Story' },
  { id: 'mission_vision', name: 'Mission & Vision' },
  { id: 'team', name: 'Team Members' },
  { id: 'statistics', name: 'Statistics' }
]

// Data loading
onMounted(async () => {
  loading.value = true
  try {
    await Promise.all([
      contentStore.fetchPageSections('about'),
      teamStore.fetchTeamMembers(),
      statStore.fetchStatistics()
    ])
    
    // Initialize sections if they don't exist
    if (!sections.value.hero) {
      sections.value.hero = { section: 'hero', data: { title: '', subtitle: '' } }
    }
    if (!sections.value.company_story) {
      sections.value.company_story = { section: 'company_story', data: { title: '', description: '' } }
    }
    if (!sections.value.mission_vision_values) {
      sections.value.mission_vision_values = { section: 'mission_vision_values', data: { title: '', description: '', mission: '', vision: '', values: [] } }
    }
  } catch (error) {
    console.error('Error loading data:', error)
  } finally {
    loading.value = false
  }
})

// Computed
const rawSections = computed(() => contentStore.pageSections.about || [])
const sections = computed(() => {
  const map = {}
  rawSections.value.forEach(s => map[s.section] = JSON.parse(JSON.stringify(s)))
  
  // Defaults
  if (!map.hero) map.hero = { section: 'hero', data: { title: '', subtitle: '' } }
  if (!map.company_story) map.company_story = { section: 'company_story', data: { title: '', description: '' } }
  if (!map.mission_vision_values) map.mission_vision_values = { section: 'mission_vision_values', data: { title: '', description: '', mission: '', vision: '', values: [] } }
  
  return map
})

const teamMembers = computed(() => teamStore.teamMembers)
const statistics = computed(() => statStore.statistics)

const missionValues = computed({
  get: () => sections.value.mission_vision_values.data.values ? sections.value.mission_vision_values.data.values.join('\n') : '',
  set: (val) => {
    sections.value.mission_vision_values.data.values = val.split('\n').filter(line => line.trim() !== '')
  }
})

// Methods
const saveSection = async (sectionName) => {
  const section = sections.value[sectionName]
  try {
    let sectionId = section.id
    
    if (section.id) {
      await contentStore.updatePageSection(section.id, {
        page: 'about',
        section: sectionName,
        data: section.data
      })
    } else {
      const newSection = await contentStore.createPageSection({
        page: 'about',
        section: sectionName,
        data: section.data
      })
      sectionId = newSection?.id
    }
    
    // Upload company story image if changed
    if (sectionName === 'company_story' && companyStoryImageFile.value && sectionId) {
      await contentStore.uploadSectionImage(sectionId, companyStoryImageFile.value)
      companyStoryImageFile.value = null
    }
    
    // Refresh
    await contentStore.fetchPageSections('about')
  } catch (error) {
    console.error('Error saving section:', error)
  }
}

// Team CRUD
const openTeamModal = (member = null) => {
  editingMember.value = member
  activeTeamTab.value = 'basic'
  profileImageFile.value = null
  
  if (member) {
    teamForm.value = JSON.parse(JSON.stringify(member))
    // Load social links
    if (member.social_links && typeof member.social_links === 'object') {
      socialLinks.value = {
        linkedin: member.social_links.linkedin || '',
        twitter: member.social_links.twitter || '',
        github: member.social_links.github || '',
        facebook: member.social_links.facebook || '',
        instagram: member.social_links.instagram || '',
        website: member.social_links.website || ''
      }
    } else {
      socialLinks.value = {
        linkedin: '',
        twitter: '',
        github: '',
        facebook: '',
        instagram: '',
        website: ''
      }
    }
  } else {
    teamForm.value = { name: '', position: '', bio: '', image: '', social_links: {}, is_active: true }
    socialLinks.value = {
      linkedin: '',
      twitter: '',
      github: '',
      facebook: '',
      instagram: '',
      website: ''
    }
  }
  showTeamModal.value = true
}

const closeTeamModal = () => {
  showTeamModal.value = false
  editingMember.value = null
  profileImageFile.value = null
}

const handleProfileImageChange = (file) => {
  profileImageFile.value = file
}

const handleCompanyStoryImageChange = (file) => {
  companyStoryImageFile.value = file
}

const saveTeamMember = async () => {
  saving.value = true
  try {
    // Prepare social links - only include non-empty values
    const filteredSocialLinks = {}
    Object.keys(socialLinks.value).forEach(key => {
      if (socialLinks.value[key] && socialLinks.value[key].trim() !== '') {
        filteredSocialLinks[key] = socialLinks.value[key]
      }
    })
    
    const formData = {
      ...teamForm.value,
      social_links: filteredSocialLinks
    }
    
    let memberId
    
    if (editingMember.value) {
      await teamStore.updateTeamMember(editingMember.value.id, formData)
      memberId = editingMember.value.id
    } else {
      const newMember = await teamStore.createTeamMember(formData)
      memberId = newMember?.id
    }
    
    // Upload profile image if changed
    if (profileImageFile.value && memberId) {
      await teamStore.uploadImage(memberId, profileImageFile.value)
      // Refresh to get updated image URL
      await teamStore.fetchTeamMembers()
    }
    
    closeTeamModal()
  } catch (error) {
    console.error('Error saving team member:', error)
  } finally {
    saving.value = false
  }
}

const deleteTeamMember = async (id) => {
  if (confirm('Are you sure?')) {
    await teamStore.deleteTeamMember(id)
  }
}

// Statistic CRUD
const openStatModal = (stat = null) => {
  editingStat.value = stat
  if (stat) {
    statForm.value = JSON.parse(JSON.stringify(stat))
  } else {
    statForm.value = { label: '', value: '', icon: '', is_active: true }
  }
  showStatModal.value = true
}

const closeStatModal = () => {
  showStatModal.value = false
  editingStat.value = null
}

const saveStatistic = async () => {
  try {
    if (editingStat.value) {
      await statStore.updateStatistic(editingStat.value.id, statForm.value)
    } else {
      await statStore.createStatistic(statForm.value)
    }
    closeStatModal()
  } catch (error) {
    console.error('Error saving statistic:', error)
  }
}

const deleteStatistic = async (id) => {
  if (confirm('Are you sure?')) {
    await statStore.deleteStatistic(id)
  }
}
</script>
